import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  TrophyIcon, UserGroupIcon, CheckCircleIcon,
  ClockIcon, ChartBarIcon,AcademicCapIcon
} from '@heroicons/react/24/outline';

import { getStats, getMyCompetitions } from '../../api/userApi.js';
import { getMyCertificates } from '../../api/certificateApi.js';

// import CompetitionRow from '../../components/CompetitionRow.jsx';
import { useAuth } from '../../contexts/AuthContext.js';
import { useToast } from '../../contexts/ToastContext.jsx';


const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const showToast = useToast();
  const [userStats, setUserStats] = useState([]);
  const [userCompetitions, setUserCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
     
    try {
      
      const [statsData, compsData, certData] = await Promise.all([
  getStats().catch(() => ({})),
  getMyCompetitions().catch(() => []),
  getMyCertificates().catch(() => ({})),
]);

const certificateCount =
  (certData.PARTICIPATION?.length || 0) +
  (certData.WINNER?.length || 0) +
  (certData.INTERNSHIP?.length || 0);
 

      setUserStats([
  { label: 'Active Arenas', value: statsData.totalCompetitions || 0, icon: TrophyIcon, color: 'from-indigo-600 to-blue-600', description: 'Registered competitions' },
  { label: 'Team Units', value: statsData.teamSize || 0, icon: UserGroupIcon, color: 'from-violet-600 to-indigo-600', description: 'Active members' },
  { label: 'Completed', value: statsData.completedCompetitions || 0, icon: CheckCircleIcon, color: 'from-emerald-600 to-teal-600', description: 'Finished competitions' },
  { label: 'Pending', value: statsData.pendingCompetitions || 0, icon: ClockIcon, color: 'from-orange-600 to-red-600', description: 'Awaiting payment' },
  { label: 'Completed Internships', value: statsData.completedInternships || 0, icon: AcademicCapIcon, color: 'from-teal-600 to-green-600', description: 'Finished internships' },
  { label: 'Ongoing Internships', value: statsData.ongoingInternships || 0, icon: ClockIcon, color: 'from-yellow-600 to-orange-500', description: 'Current internships' },
  { label: 'Certificates', value: certificateCount ||0, icon: TrophyIcon, color: 'from-indigo-500 to-purple-500', description: 'Earned certificates' },
]);


      setUserCompetitions(compsData);
    } catch (err) {
      showToast('Failed to load dashboard data','error');
    }
  }, [showToast]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (!user){
        const savedUser = localStorage.getItem('user');
        if (!savedUser) {
          navigate('/login');
          return;
        }
      }
      
      try {
        await fetchData();
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user, navigate, fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] bg-[#050505]">
        <div className="text-center">
          <div className="h-10 w-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mb-4 mx-auto"></div>
          <p className="text-indigo-400 font-black italic uppercase tracking-widest text-[10px]">Syncing Data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-700 bg-[#050505]">
      {/* Welcome Section */}
      <div className="mb-10">
        <h1 className="text-3xl lg:text-5xl font-black text-white italic uppercase tracking-tighter mb-2">
          Welcome back, <span className="text-indigo-500">
          {(user?.name || user?.full_name || user?.email || 'User').split(' ')[0]}  
          </span>
        </h1>
        <p className="text-gray-500 text-xs font-bold uppercase tracking-[0.2em] italic">Operational Summary & Activity Feed</p>
      </div>

      {/* Stats Grid */}
      {/* Stats Grid */}
<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4 mb-10 auto-rows-fr">
  {userStats.map((stat, index) => {
    const Icon = stat.icon;
    return (
      <motion.div
        key={stat.label}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.08 }}
        className="flex flex-col justify-between bg-[#0a0a0a] border border-white/5 rounded-2xl p-6 hover:border-indigo-500/30 transition-all group min-h-[120px]"
      >
        <div className="flex items-center justify-between mb-3">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} shadow-lg shadow-indigo-500/10 flex-shrink-0`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
          <span className="text-[8px] font-black italic uppercase tracking-widest text-gray-400 group-hover:text-indigo-400 transition-colors text-right">
            {stat.description}
          </span>
        </div>
        <h3 className="text-gray-500 text-[9px] font-black uppercase tracking-widest mb-1 italic">{stat.label}</h3>
        <p className="text-2xl md:text-3xl font-black text-white italic tracking-tighter">{stat.value}</p>
      </motion.div>
    );
  })}
</div>

      {/* Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0a0a0a] border border-white/5 rounded-[2rem] p-8">
          <h3 className="text-[10px] font-black text-white mb-6 flex items-center gap-3 uppercase tracking-[0.2em] italic">
            <ClockIcon className="h-4 w-4 text-orange-500" /> Deadlines
          </h3>
          <p className="text-[10px] text-gray-600 font-bold italic uppercase tracking-widest">No immediate action required.</p>
        </div>

        <div className="bg-[#0a0a0a] border border-white/5 rounded-[2rem] p-8">
          <h3 className="text-[10px] font-black text-white mb-6 flex items-center gap-3 uppercase tracking-[0.2em] italic">
            <ChartBarIcon className="h-4 w-4 text-indigo-500" /> Activity Feed
          </h3>
          <div className="text-[10px] text-gray-600 font-bold italic uppercase tracking-widest">System status nominal.</div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;